import React, { useState } from 'react';
import {
  ChevronRight,
  Shield,
  Headphones,
  Award,
  Star,
  Clock,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  ShieldCheck,
  Leaf,
  Sparkles,
  TrendingUp,
  Target,
  Users,
  Settings,
  ThumbsUp,
  CheckCircle,
  Quote,
} from 'lucide-react';

const colors = {
  primary: '#FF8A00',
  primaryDark: '#FF6A00',
  primaryLight: '#FFB45A',
  primarySoft: '#FFF4E6',
  bgLight: '#FFF8F0',
  textPrimary: '#1A1A1A',
  textMuted: '#555555',
  borderSoft: 'rgba(255, 138, 0, 0.16)',
  shadowSoft: '0 18px 45px rgba(0, 0, 0, 0.08)',
  gradientSummer: 'linear-gradient(135deg, #FF8A00 0%, #FFB45A 100%)',
  gradientHero: 'linear-gradient(135deg, #FFE8CC 0%, #FFF4E6 45%, #FFE8CC 100%)',
};

function HomePage({ setCurrentPage }) {
  const stats = [
    { value: '15,000+', label: 'Happy Families', icon: <Users />, color: '#FF8A00' },
    { value: '40%', label: 'Energy Saving', icon: <TrendingUp />, color: '#00C853' },
    { value: '48 Hrs', label: 'Service Support', icon: <Clock />, color: '#FF6D00' },
  ];

  const features = [
    { 
      icon: <Shield />, 
      title: 'Premium Quality', 
      desc: 'ISO 9001:2015 certified manufacturing with rigorous quality control',
      points: ['ISO Certified', 'Rigorous Testing', 'Durable Materials']
    },
    { 
      icon: <Leaf />, 
      title: 'Energy Efficient', 
      desc: 'Designed for lower electricity bills and environmental sustainability',
      points: ['Lower Bills', 'ECO Modes', '5-Star Rated']
    },
    { 
      icon: <Settings />, 
      title: 'Easy Maintenance', 
      desc: 'Simple to clean and service with readily available spare parts',
      points: ['Easy Cleaning', 'Spare Parts', 'DIY Friendly']
    },
    { 
      icon: <ThumbsUp />, 
      title: 'Customer Support', 
      desc: '48-hour service response with trained technicians across India',
      points: ['48Hr Response', 'Trained Technicians', 'Pan-India Service']
    },
  ];

  const promises = [
    {
      icon: <Award size={32} />,
      title: 'Premium Build Quality',
      description: 'Each product undergoes rigorous testing for durability and performance in Indian conditions.',
      gradient: 'linear-gradient(135deg, #FF8A00, #FFB45A)',
    },
    {
      icon: <Shield size={32} />,
      title: 'Fair & Honest Pricing',
      description: 'Modern design and robust quality at prices that respect your budget.',
      gradient: 'linear-gradient(135deg, #00C853, #64DD17)',
    },
    {
      icon: <Headphones size={32} />,
      title: 'Reliable After-Sales',
      description: 'Responsive support network to ensure your appliances run smoothly for years.',
      gradient: 'linear-gradient(135deg, #2962FF, #448AFF)',
    },
    {
      icon: <Leaf size={32} />,
      title: 'Energy Smart & Green',
      description: 'Optimized for lower power consumption to reduce your bills and environmental impact.',
      gradient: 'linear-gradient(135deg, #00B8D4, #40C4FF)',
    },
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Homeowner, Delhi',
      text: 'Summer Beat fans have been running non-stop for 3 years without any issues. The best quality at affordable prices!',
      rating: 5,
      purchase: 'Ceiling Fan',
    },
    {
      name: 'Priya Sharma',
      role: 'Homemaker, Ghaziabad',
      text: "Their winter heaters kept my family warm through Delhi's coldest winter. Energy efficient and safe for kids.",
      rating: 5,
      purchase: 'Quartz Heater',
    },
    {
      name: 'Amit Patel',
      role: 'Business Owner, Noida',
      text: 'The air coolers are perfect for our home. Cools effectively while consuming very little electricity.',
      rating: 4.5,
      purchase: 'Air Cooler',
    },
    {
      name: 'Sanjay Verma',
      role: 'Retired Officer, Gurgaon',
      text: 'Excellent customer service. When I had an issue, they sent a technician within 24 hours and resolved it.',
      rating: 5,
      purchase: 'Mixer Grinder',
    },
  ];

  return (
    <div style={{ overflow: 'hidden', background: colors.bgLight }}>
      {/* Hero Section */}
      <section
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: colors.gradientHero,
          padding: '3rem 2rem',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Elements */}
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255, 138, 0, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(0, 184, 212, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'float 8s ease-in-out infinite 1s',
          }}
        />

        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'center',
          }}
        >
          {/* Hero Content */}
          <div
            style={{
              animation: 'fadeInUp 0.8s ease-out',
              zIndex: 2,
            }}
          >
            {/* Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '0.5rem 1rem',
                borderRadius: '50px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(255, 138, 0, 0.2)',
                marginBottom: '2rem',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(255, 138, 0, 0.1)',
              }}
            >
              <Sparkles size={18} color={colors.primary} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: colors.primaryDark,
                  letterSpacing: '0.05em',
                }}
              >
                INDIA'S TRUSTED HOME APPLIANCE BRAND
              </span>
            </div>

            {/* Main Heading */}
            <h1
              style={{
                fontSize: 'clamp(2.8rem, 6vw, 4.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                marginBottom: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              <span
                style={{
                  display: 'block',
                  background: colors.gradientSummer,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '0.5rem',
                }}
              >
                Cool Summers
              </span>
              <span
                style={{
                  display: 'block',
                  color: colors.textPrimary,
                  marginBottom: '0.5rem',
                }}
              >
                Warm Winters
              </span>
              <span
                style={{
                  fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                  fontWeight: 600,
                  color: colors.textMuted,
                  opacity: 0.9,
                }}
              >
                Perfect comfort for every Indian home
              </span>
            </h1>

            {/* Description */}
            <p
              style={{
                fontSize: '1.1rem',
                color: colors.textMuted,
                lineHeight: 1.8,
                marginBottom: '2.5rem',
                maxWidth: '600px',
                opacity: 0.9,
              }}
            >
              Summer Beat brings modern, energy-efficient fans, heaters, and coolers designed 
              for real Indian households. Built for long-term reliability and everyday comfort.
            </p>

            {/* Stats */}
            <div
              style={{
                display: 'flex',
                gap: '2rem',
                marginBottom: '3rem',
                flexWrap: 'wrap',
              }}
            >
              {stats.map((stat, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      background: `${stat.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: colors.textPrimary }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: colors.textMuted, opacity: 0.8 }}>
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => setCurrentPage('products')}
                style={{
                  background: colors.gradientSummer,
                  color: 'white',
                  padding: '1rem 2.5rem',
                  borderRadius: '50px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 10px 40px rgba(255, 138, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(255, 138, 0, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(255, 138, 0, 0.4)';
                }}
              >
                <span>Explore Our Products</span>
                <ChevronRight size={20} />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateX(100%)';
                  }}
                />
              </button>

              <button
                onClick={() => setCurrentPage('contact')}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: colors.textPrimary,
                  padding: '1rem 2rem',
                  borderRadius: '50px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  border: `2px solid ${colors.borderSoft}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = colors.primary;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = colors.borderSoft;
                }}
              >
                <Phone size={18} />
                <span>Contact Us</span>
              </button>
            </div>
          </div>

          {/* Hero Image - Landscape Optimized */}
          <div
            style={{
              position: 'relative',
              height: '500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'fadeInRight 1s ease-out',
            }}
          >
            {/* Main Image Container */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                maxWidth: '800px',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 40px 80px rgba(255, 138, 0, 0.2)',
                border: '1px solid rgba(255, 138, 0, 0.1)',
              }}
            >
              {/* Main Image - Landscape Banner */}
              <img
                src="./Banner.png"
                alt="Summer Beat Home Appliances - Fans, Heaters, Coolers"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: linear-gradient(135deg, ${colors.gradientSummer}); color: white; font-size: 2rem; font-weight: bold; border-radius: 24px; padding: 2rem; text-align: center;">
                      <div style="margin-bottom: 1rem; font-size: 3rem;">🏠</div>
                      Summer Beat Home Appliances
                      <div style="font-size: 1.2rem; margin-top: 1rem; opacity: 0.9;">
                        Cool Summers • Warm Winters
                      </div>
                    </div>
                  `;
                }}
              />
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(40px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeInRight {
            from { opacity: 0; transform: translateX(40px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-15px); }
          }
          @media (max-width: 1024px) {
            div[style*="gridTemplateColumns"] {
              grid-template-columns: 1fr !important;
              text-align: center;
            }
            div[style*="height: 500px"] {
              height: 300px !important;
              margin-top: 3rem;
            }
          }
        `}</style>
      </section>

      {/* Features Section */}
      <section
        style={{
          padding: '6rem 2rem',
          background: 'white',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.5rem',
                borderRadius: '50px',
                background: `${colors.primary}10`,
                marginBottom: '1.5rem',
              }}
            >
              <Star size={18} color={colors.primary} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: colors.primary,
                  letterSpacing: '0.05em',
                }}
              >
                WHY CHOOSE US
              </span>
            </div>
            <h2
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '1rem',
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              Built for{' '}
              <span
                style={{
                  background: colors.gradientSummer,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Indian Homes
              </span>
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: colors.textMuted,
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              Engineered for Indian climate, designed for comfort, priced for everyone
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
            }}
          >
            {features.map((feature, i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  padding: '2.5rem 2rem',
                  borderRadius: '20px',
                  border: `1px solid ${colors.borderSoft}`,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(255, 138, 0, 0.15)';
                  e.currentTarget.style.borderColor = colors.primary;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = colors.borderSoft;
                }}
              >
                <div
                  style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '20px',
                    background: colors.gradientSummer,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    color: 'white',
                  }}
                >
                  {feature.icon}
                </div>
                <h3
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    marginBottom: '1rem',
                    color: colors.textPrimary,
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    color: colors.textMuted,
                    lineHeight: 1.6,
                    fontSize: '1rem',
                    marginBottom: '1.5rem',
                  }}
                >
                  {feature.desc}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem' }}>
                  {feature.points.map((point, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: `${colors.primary}10`,
                        color: colors.primaryDark,
                        padding: '0.4rem 0.8rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                      }}
                    >
                      {point}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        style={{
          padding: '6rem 2rem',
          background: colors.bgLight,
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.5rem',
                borderRadius: '50px',
                background: `${colors.primary}10`,
                marginBottom: '1.5rem',
              }}
            >
              <Quote size={18} color={colors.primary} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: colors.primary,
                  letterSpacing: '0.05em',
                }}
              >
                CUSTOMER REVIEWS
              </span>
            </div>
            <h2
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '1rem',
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              What Our{' '}
              <span
                style={{
                  background: colors.gradientSummer,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Customers Say
              </span>
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: colors.textMuted,
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              Real feedback from families across India who trust Summer Beat
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
            }}
          >
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  padding: '2.5rem 2rem',
                  borderRadius: '20px',
                  border: `1px solid ${colors.borderSoft}`,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(255, 138, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.05)';
                }}
              >
                {/* Quote Icon */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '2rem',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: colors.gradientSummer,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <Quote size={20} />
                </div>

                {/* Rating */}
                <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '1.5rem' }}>
                  {[...Array(5)].map((_, idx) => (
                    <Star
                      key={idx}
                      size={16}
                      fill={idx < Math.floor(testimonial.rating) ? colors.primary : 'none'}
                      color={idx < Math.floor(testimonial.rating) ? colors.primary : colors.borderSoft}
                    />
                  ))}
                  {testimonial.rating % 1 !== 0 && (
                    <span style={{ marginLeft: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                      {testimonial.rating.toFixed(1)}
                    </span>
                  )}
                </div>

                {/* Testimonial Text */}
                <p
                  style={{
                    color: colors.textPrimary,
                    lineHeight: 1.7,
                    fontSize: '1.05rem',
                    fontStyle: 'italic',
                    marginBottom: '2rem',
                  }}
                >
                  "{testimonial.text}"
                </p>

                {/* Customer Info */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: colors.textPrimary, fontSize: '1.1rem' }}>
                      {testimonial.name}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                      {testimonial.role}
                    </div>
                  </div>
                  <div
                    style={{
                      background: `${colors.primary}10`,
                      color: colors.primaryDark,
                      padding: '0.4rem 0.8rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    {testimonial.purchase}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Reviews Button */}
          <div style={{ textAlign: 'center', marginTop: '4rem' }}>
            <button
              onClick={() => setCurrentPage('contact')}
              style={{
                background: 'transparent',
                color: colors.primary,
                padding: '1rem 2.5rem',
                borderRadius: '50px',
                fontSize: '1rem',
                fontWeight: 600,
                border: `2px solid ${colors.borderSoft}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.8rem',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = colors.gradientSummer;
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(255, 138, 0, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.primary;
                e.currentTarget.style.borderColor = colors.borderSoft;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Read More Reviews
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section
        style={{
          padding: '6rem 2rem',
          background: '#1A1A1A',
          color: 'white',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.5rem',
                borderRadius: '50px',
                background: 'rgba(255, 138, 0, 0.1)',
                marginBottom: '1.5rem',
              }}
            >
              <ShieldCheck size={18} color={colors.primary} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: colors.primary,
                }}
              >
                OUR PROMISE
              </span>
            </div>
            <h2
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '1rem',
                letterSpacing: '-0.02em',
              }}
            >
              Why Choose{' '}
              <span
                style={{
                  background: colors.gradientSummer,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Summer Beat
              </span>
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                opacity: 0.85,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              Thoughtfully designed appliances that balance technology, trust, and everyday usability.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2.5rem',
            }}
          >
            {promises.map((promise, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  backdropFilter: 'blur(12px)',
                  padding: '2.5rem',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255, 138, 0, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                {/* Gradient Accent */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: promise.gradient,
                  }}
                />

                <div
                  style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '20px',
                    background: 'rgba(255, 138, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    color: colors.primary,
                  }}
                >
                  {promise.icon}
                </div>
                <h3
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    marginBottom: '1rem',
                  }}
                >
                  {promise.title}
                </h3>
                <p
                  style={{
                    opacity: 0.85,
                    lineHeight: 1.7,
                    fontSize: '1rem',
                  }}
                >
                  {promise.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section - Enhanced */}
      <section
        style={{
          padding: '6rem 2rem',
          background: 'white',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.5rem',
                borderRadius: '50px',
                background: `${colors.primary}10`,
                marginBottom: '1.5rem',
              }}
            >
              <Phone size={18} color={colors.primary} />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: colors.primary,
                }}
              >
                GET IN TOUCH
              </span>
            </div>
            <h2
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '1rem',
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
              }}
            >
              Ready for{' '}
              <span
                style={{
                  background: colors.gradientSummer,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Perfect Comfort?
              </span>
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: colors.textMuted,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              Contact India's trusted home appliances manufacturer
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '3rem',
              alignItems: 'start',
            }}
          >
            {/* Contact Info */}
            <div
              style={{
                padding: '2.5rem',
                borderRadius: '20px',
                border: `1px solid ${colors.borderSoft}`,
                background: colors.bgLight,
              }}
            >
              <h3
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '2rem',
                  color: colors.textPrimary,
                }}
              >
                Contact Information
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: `${colors.primary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.primary,
                      flexShrink: 0,
                    }}
                  >
                    <MapPin size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem' }}>
                      Address
                    </div>
                    <div style={{ color: colors.textMuted, lineHeight: 1.5, fontSize: '0.95rem' }}>
                      Khasra No.1160, Sihani Gate<br />
                      Meerut Road, Ghaziabad<br />
                      Uttar Pradesh – 201001, India
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: `${colors.primary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.primary,
                      flexShrink: 0,
                    }}
                  >
                    <Mail size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem' }}>
                      Email
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.95rem' }}>
                      summerbeathomeappliance@gmail.com
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: `${colors.primary}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.primary,
                      flexShrink: 0,
                    }}
                  >
                    
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Form */}
            <div>
              <h3
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '2rem',
                  color: colors.textPrimary,
                }}
              >
                Send us a message
              </h3>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.5rem',
                }}
              >
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    style={{
                      width: '100%',
                      padding: '1rem 1.2rem',
                      borderRadius: '12px',
                      border: `1px solid ${colors.borderSoft}`,
                      fontSize: '1rem',
                      background: colors.bgLight,
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderSoft;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    style={{
                      width: '100%',
                      padding: '1rem 1.2rem',
                      borderRadius: '12px',
                      border: `1px solid ${colors.borderSoft}`,
                      fontSize: '1rem',
                      background: colors.bgLight,
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderSoft;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <textarea
                    placeholder="Your Message"
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '1rem 1.2rem',
                      borderRadius: '12px',
                      border: `1px solid ${colors.borderSoft}`,
                      fontSize: '1rem',
                      background: colors.bgLight,
                      resize: 'vertical',
                      transition: 'all 0.3s ease',
                      minHeight: '100px',
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.borderSoft;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <button
                  onClick={() => setCurrentPage('contact')}
                  style={{
                    background: colors.gradientSummer,
                    color: 'white',
                    padding: '1rem 2rem',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.8rem',
                    boxShadow: '0 10px 30px rgba(255, 138, 0, 0.3)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(255, 138, 0, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(255, 138, 0, 0.3)';
                  }}
                >
                  Send Message
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;